class RocToggleGender extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        const toggle = this.querySelector('#gender-toggle');
        if (!toggle) {
            return;
        }

        // Apply the checkbox's initial state (it may already render `checked`
        // server-side, e.g. from a collection URL containing "woman") — the
        // `change` event only fires on user interaction, never on load.
        this.applyState(toggle.checked);

        // The browser can restore a previously-checked value onto this input
        // (bfcache back/forward navigation, or its own form-state restoration
        // on a fresh reload) *after* connectedCallback already ran, silently
        // flipping `.checked` without firing `change`. `pageshow` fires once
        // that restoration has settled, for both cases, so re-sync there too.
        window.addEventListener('pageshow', () => this.applyState(toggle.checked));

        toggle.addEventListener('change', (event) => {
            const isChecked = event.target.checked;

            this.applyState(isChecked);

            // Persist the manual choice so the PDP toggle reflects it when the
            // customer navigates from this collection to a product page.
            // `keepalive` ensures the request still completes on the server even
            // if the browser starts navigating away (e.g. clicking a product
            // link) right after the toggle click, before the fetch would
            // otherwise resolve.
            fetch(window.Shopify.routes.root + 'cart/update.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    attributes: {
                        'is_women': isChecked ? 'true' : ''
                    }
                }),
                keepalive: true
            }).catch(() => {});
        });
    }
    applyState(isChecked) {
        const container = this.closest('.roc-toggle-render');
        if (!container) return;
        const productCards = container.querySelectorAll('.product-card');

        productCards.forEach(card => {
            const womanSlide = card.querySelector('.product-image--women');
            const allSlides = card.querySelectorAll('.product-card__slide');

            if (isChecked) {
                if (womanSlide) {
                    card.classList.add("is_woman");
                    allSlides.forEach(s => s.style.display = 'none');
                    womanSlide.style.display = 'block';
                } else {
                    this.showDefaultSlide(allSlides);
                }
            } else {
                card.classList.remove("is_woman");
                this.showDefaultSlide(allSlides);
            }
        });
    }
    showDefaultSlide(slides) {
        slides.forEach((s, index) => {
            if (index === 0) {
                s.style.display = 'block';
            } else {
                s.style.display = 'none';
            }
        });
    }
}

customElements.define("toggle-gender", RocToggleGender);

class RocToggleGenderPDP extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('#gender-toggle');
    this.container = this.closest('.roc-toggle-render');

    if (!this.toggle || !this.container) return;

    this.slides = [
      ...this.container.querySelectorAll('.product-gallery__media'),
    ];

    this.handleToggle = this.handleToggle.bind(this);
    this.persistChoice = this.persistChoice.bind(this);

    this.toggle.addEventListener('change', this.handleToggle);
    this.toggle.addEventListener('change', this.persistChoice);

    // See RocToggleGender for why: the browser can restore a previously
    // checked value onto this input after connectedCallback already ran
    // (bfcache navigation, or its own form-state restoration on a fresh
    // reload), without firing `change`. Re-sync once that has settled.
    window.addEventListener('pageshow', this.handleToggle);

    this.handleToggle();
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('change', this.handleToggle);
    this.toggle?.removeEventListener('change', this.persistChoice);
    window.removeEventListener('pageshow', this.handleToggle);
  }

  // Persist the manual choice so it survives leaving the PDP (home,
  // collections, other PDPs). `keepalive` lets it finish during navigation.
  persistChoice() {
    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: { is_women: this.toggle.checked ? 'true' : '' }
      }),
      keepalive: true
    }).catch(() => {});
  }

  handleToggle() {
    const isWoman = this.toggle.checked;

    // Woman mode order: #woman, #packshot, untagged (#man hidden).
    // Man mode order: #packshot, #man, untagged (#woman hidden).
    const rankWoman = { woman: 0, packshot: 1, '': 2, man: 3 };
    const rankMan = { packshot: 0, man: 1, '': 2, woman: 3 };
    const rank = isWoman ? rankWoman : rankMan;

    const sortedSlides = [...this.slides].sort(
      (a, b) => rank[a.dataset.mediaGender] - rank[b.dataset.mediaGender]
    );

    const parent = this.slides[0]?.parentNode;
    if (parent) {
      sortedSlides.forEach((slide) => parent.appendChild(slide));
    }

    this.slides.forEach((slide) => {
      const gender = slide.dataset.mediaGender;

      slide.classList.toggle('is_woman', isWoman && gender === 'woman');

      // Woman mode hides #man images; man mode hides #woman images. Untagged
      // and #packshot images are always shown in both modes.
      slide.hidden = isWoman ? gender === 'man' : gender === 'woman';
    });

    // On the mobile scroll-snap carousel, the browser's scroll anchoring would
    // otherwise silently keep the previously-visible slide's scroll position
    // after slides are reordered/hidden/shown. Force it back to the start so
    // the new first visible slide is actually the one shown.
    const carousel = this.container.querySelector('.product-gallery__carousel');
    if (carousel) {
      carousel.scrollTo({ left: 0, behavior: 'auto' });
    }
  }
}

if (!window.customElements.get("toggle-gender-pdp")) {
  window.customElements.define("toggle-gender-pdp", RocToggleGenderPDP);
}